import type { FormInstance } from "antd";
import { cleanupDiscardedCmsAssetsAction } from "@innovate/cms-admin-ui";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  type ReactNode,
} from "react";

export type EditorSaveHandler = () => Promise<unknown>;

export type EditorDraftContextValue = {
  hasLocalChanges: boolean;
  markClean: () => void;
  markDirty: () => void;
  discardCurrentDraft: (options?: { showMessage?: boolean }) => void;
  registerEditorSave: (handler: EditorSaveHandler) => () => void;
  registerEditorDiscard: (handler: () => void) => () => void;
  saveCurrentDraft: (options?: { showSuccess?: boolean }) => Promise<void>;
};

const noop = () => undefined;

export const EditorDraftContext = createContext<EditorDraftContextValue>({
  hasLocalChanges: false,
  markClean: noop,
  markDirty: noop,
  discardCurrentDraft: noop,
  registerEditorSave: () => noop,
  registerEditorDiscard: () => noop,
  saveCurrentDraft: async () => undefined,
});

export function EditorDraftProvider({
  value,
  children,
}: {
  value: EditorDraftContextValue;
  children: ReactNode;
}) {
  return (
    <EditorDraftContext.Provider value={value}>
      {children}
    </EditorDraftContext.Provider>
  );
}

export function useEditorDraftContext() {
  return useContext(EditorDraftContext);
}

export function useCmsEditorDraft<T>(
  form: FormInstance<T>,
  persist: (values: T) => Promise<unknown>,
  initialValues?: T,
) {
  const {
    hasLocalChanges,
    markClean,
    markDirty,
    registerEditorDiscard,
    registerEditorSave,
  } = useEditorDraftContext();
  const persistRef = useRef(persist);
  const snapshotRef = useRef<T | undefined>(
    initialValues == null
      ? undefined
      : JSON.parse(JSON.stringify(initialValues)) as T,
  );
  persistRef.current = persist;

  useEffect(() => {
    if (snapshotRef.current == null) {
      snapshotRef.current = JSON.parse(
        JSON.stringify(form.getFieldsValue(true)),
      ) as T;
    }
  }, [form]);

  useEffect(() => {
    if (initialValues == null || hasLocalChanges) return;
    snapshotRef.current = JSON.parse(JSON.stringify(initialValues)) as T;
  }, [hasLocalChanges, initialValues]);

  const saveValues = useCallback(
    async (values: T) => {
      await persistRef.current(values);
      snapshotRef.current = JSON.parse(JSON.stringify(values)) as T;
      markClean();
    },
    [markClean],
  );

  useEffect(
    () => {
      const unregisterSave = registerEditorSave(async () => {
        const values = await form.validateFields();
        await saveValues(values);
      });
      const unregisterDiscard = registerEditorDiscard(() => {
        const discardedValues = form.getFieldsValue(true);
        if (snapshotRef.current != null) {
          const restoredValues = JSON.parse(JSON.stringify(snapshotRef.current)) as Parameters<
              typeof form.setFieldsValue
            >[0];
          form.setFieldsValue(restoredValues);
          void cleanupDiscardedCmsAssetsAction(discardedValues, restoredValues);
        }
        markClean();
      });
      return () => {
        unregisterSave();
        unregisterDiscard();
      };
    },
    [form, markClean, registerEditorDiscard, registerEditorSave, saveValues],
  );

  return { markDirty, saveValues };
}
